import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, RotateCcw, Repeat, Mic, MessageSquare, PhoneOff, Info, Star } from "lucide-react";
import { api, useApi, errMsg } from "@/lib/api";
import { scheduleWaitingNotice, WAITING_TEXT } from "@/lib/waitingNotice.mjs";
import { createStudioSession } from "@/lib/studioSession.mjs";
import { useVoice } from "@/hooks/useVoice";
import { PageHeader, Card, Field, Sel, StatusBadge } from "@/components/common";
import { MessageTrace, EvalDialog } from "@/components/Trace";
import Markdown from "@/components/Markdown";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ORB = { idle: ["bg-muted", "Session vocale inactive"], listening: ["bg-sky-400 shadow-[0_0_35px_rgba(56,189,248,0.6)] orb-listening", "Écoute"],
  processing: ["bg-indigo-400 shadow-[0_0_35px_rgba(129,140,248,0.7)] orb-processing", "Traitement"], speaking: ["bg-emerald-400 shadow-[0_0_35px_rgba(52,211,153,0.6)] orb-speaking", "Réponse audio"] };

function Bubble({ m, active, onSelect, onEval }) {
  const user = m.role === "user";
  return (
    <div className={`flex ${user ? "justify-end" : "justify-start"} fade-in`}>
      <div onClick={() => !user && onSelect(m)} data-testid={user ? "chat-user-message" : "chat-assistant-message"}
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${user ? "bg-primary text-primary-foreground" : `bg-card border cursor-pointer ${active ? "ring-2 ring-primary" : "hover:border-primary/50"}`}`}>
        {!user && <div className="text-[11px] text-muted-foreground mb-1 flex gap-2 items-center"><b className="text-foreground" data-testid="chat-agent-name">{m.agent_name}</b>{m.channel === "voice" && <Mic className="h-3 w-3" />}<span>{m.latency_ms} ms</span>{m.interrupted && <span className="text-amber-500">interrompue</span>}</div>}
        {user && m.channel === "voice" && <Mic className="h-3 w-3 inline mr-1 opacity-70" />}
        {user ? <div className="whitespace-pre-wrap">{m.content}</div> : <Markdown>{m.content}</Markdown>}
        {!user && m.sources?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{m.sources.map((s) => <span key={s.ref} className="text-[10px] border rounded px-1.5 py-0.5 text-muted-foreground" data-testid="chat-source-chip" title={s.text}>[{s.ref}] {s.document_title}</span>)}</div>}
        {!user && m.error && <div className="text-[11px] text-rose-400 mt-1">⚠ {m.error.slice(0, 160)}</div>}
        {!user && m.id && <button className="text-[11px] text-muted-foreground hover:text-primary mt-2 flex items-center gap-1" onClick={(e) => { e.stopPropagation(); onEval(m); }} data-testid="chat-eval-btn"><Star className="h-3 w-3" />Évaluer</button>}
      </div>
    </div>
  );
}

export default function Studio() {
  const { data: agents } = useApi("/agents");
  const { data: vstatus } = useApi("/voice/status");
  const [entry, setEntry] = useState("");
  const [mode, setMode] = useState("draft");
  const [commonMode, setCommonMode] = useState("published");
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [waitingText, setWaitingText] = useState("");
  const waitingCancel = useRef(null);
  const [tab, setTab] = useState("text");
  const [sel, setSel] = useState(null);
  const [evalMsg, setEvalMsg] = useState(null);
  const endRef = useRef(null);
  const session = useRef(null);
  if (!session.current) session.current = createStudioSession(api, setConv, (m) => {
    setMsgs((items) => [...items, m]); setSel(m);
  });
  const studioAuth = useAuth();
  const canUse = studioAuth.can("studio:use");
  useEffect(() => { if (agents && !entry) setEntry(agents.find((a) => a.kind === "accueil")?.id || agents[0]?.id); }, [agents, entry]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const entryAgent = agents?.find((a) => a.id === entry);

  const ensureConv = (channel = "text") => session.current.begin({ entry, mode, channel, commonMode });
  const startText = async () => {
    setBusy(true);
    try { await ensureConv("text"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const sendText = async (t, channel) => {
    const ticket = session.current.ticket();
    const c = await ensureConv(channel);
    if (!session.current.valid(ticket)) throw new Error("Session modifiée");
    setMsgs((m) => [...m, { role: "user", content: t, channel, _k: Date.now() }]);
    waitingCancel.current?.();
    const cancelWaiting = scheduleWaitingNotice({
      isCurrent: () => session.current.valid(ticket) && session.current.isCurrent(c.id),
      load: () => WAITING_TEXT,
      play: (notice) => { setWaitingText(notice); return () => setWaitingText(""); },
    });
    waitingCancel.current = cancelWaiting;
    let r;
    try {
      r = await api.post(`/studio/conversations/${c.id}/messages`, { text: t, channel });
    } finally {
      cancelWaiting();
      if (waitingCancel.current === cancelWaiting) waitingCancel.current = null;
    }
    if (!session.current.valid(ticket) || !session.current.isCurrent(c.id)) throw new Error("Session modifiée");
    setMsgs((m) => [...m, r.data]);
    setSel(r.data);
    return r.data;
  };
  const submit = async (e) => {
    e?.preventDefault();
    if (!text.trim() || busy || voice.state !== "idle") return;
    const t = text;
    setText(""); setBusy(true);
    try { await sendText(t, "text"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const speech = async (msg) => {
    if (!vstatus?.server_voice) return { speakText: msg.oral_text };
    const audio = await api.post("/voice/tts", { text: msg.oral_text, voice: msg.voice?.voice, model: msg.voice?.tts_model, speed: msg.voice?.speed }, { responseType: "blob" });
    return { audioUrl: URL.createObjectURL(audio.data) };
  };
  const voice = useVoice({
    onWaiting: () => speech({ oral_text: WAITING_TEXT }),
    onStart: async () => {
      const existing = !!conv;
      const c = await ensureConv("voice");
      return existing ? null : speech(c.opening);
    },
    serverVoice: !!vstatus?.server_voice,
    onInterrupt: () => setMsgs((m) => {
      const last = m[m.length - 1];
      if (last?.role === "assistant" && last.id && conv) api.post(`/studio/conversations/${conv.id}/messages/${last.id}/interrupted`).catch(() => {});
      return m.map((x, i) => (i === m.length - 1 && x.role === "assistant" ? { ...x, interrupted: true } : x));
    }),
    onUtterance: async (blob, transcript) => {
      const ticket = session.current.ticket();
      let t = transcript;
      if (blob) {
        const fd = new FormData();
        fd.append("file", blob, "parole.webm");
        fd.append("language", "fr");
        t = (await api.post("/voice/stt", fd)).data.text;
      }
      if (!session.current.valid(ticket) || !t?.trim()) return null;
      const msg = await sendText(t, "voice");
      return speech(msg);
    },
  });
  useEffect(() => {
    waitingCancel.current?.();
    voice.stop(); session.current.reset(); setConv(null); setMsgs([]); setSel(null);
    return () => { waitingCancel.current?.(); session.current.reset(); voice.stop(); };
  }, [entry, mode, commonMode, voice.stop]);
  useEffect(() => { if (voice.error) toast.error(voice.error); }, [voice.error]);
  const reset = async () => {
    waitingCancel.current?.();
    voice.stop();
    session.current.reset();
    setConv(null); setMsgs([]); setSel(null);
  };
  const replay = async () => {
    if (!conv) return;
    setBusy(true);
    try {
      const r = await api.post(`/studio/conversations/${conv.id}/replay`);
      session.current.adopt(r.data.conversation);
      setConv(r.data.conversation);
      const out = [];
      r.data.messages.forEach((m, i) => { out.push({ role: "user", content: msgs.filter((x) => x.role === "user")[i]?.content, _k: i }); out.push(m); });
      setMsgs(out);
      toast.success("Scénario rejoué dans une nouvelle conversation");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const [orbCls, orbLabel] = ORB[voice.state];
  return (
    <div>
      <PageHeader title="Studio de test" subtitle="Même moteur multi-agents, mêmes outils, skills et documents que les futurs appels téléphoniques." />
      <div className="grid xl:grid-cols-[260px_1fr_360px] gap-4">
        <Card className="space-y-4 h-fit">
          <Field label="Agent testé (ou routage global)">
            <Sel testid="studio-agent-select" value={entry} onChange={(v) => { if (!busy && voice.state === "idle") setEntry(v); }} options={(agents || []).map((a) => ({ value: a.id, label: `${a.name}${a.kind === "accueil" ? " — routage global" : ""}` }))} />
          </Field>
          <Field label="Configuration" helpTopic="versions-agents">
            <Sel testid="studio-mode-select" value={mode} onChange={(v) => { if (!busy && voice.state === "idle") setMode(v); }} options={[{ value: "draft", label: "Brouillon" }, { value: "test", label: "Version de test" }, { value: "published", label: "Version publiée" }]} />
          </Field>
          <Field label="Prompt chapeau commun" hint="Version figée au démarrage de la conversation. Le choix du chapeau est indépendant de la version de l’agent.">
            <Sel value={commonMode} onChange={(v) => { if (!busy && voice.state === 'idle') setCommonMode(v); }} options={[{value:'published',label:'Chapeau publié'}, {value:'test',label:'Chapeau de test'}, ...(studioAuth.can('agents:write') ? [{value:'draft',label:'Brouillon du chapeau'}] : [])]} />
            <p className="text-xs">{conv?.common_prompt_snapshot?.label}</p>
          </Field>
          <div className="flex flex-col gap-2">
            <Button onClick={tab === "voice" ? voice.start : startText} disabled={!entry || !!conv || busy || !canUse || voice.state !== "idle"} data-testid="studio-start-btn">Démarrer la conversation</Button>
            <Button variant="outline" disabled={busy} onClick={reset} data-testid="studio-reset-btn"><RotateCcw className="h-4 w-4 mr-1" />Réinitialiser</Button>
            <Button variant="outline" onClick={replay} disabled={!conv || busy || voice.state !== "idle"} data-testid="studio-replay-btn"><Repeat className="h-4 w-4 mr-1" />Rejouer le scénario</Button>
          </div>
          <div className="text-[11px] text-muted-foreground space-y-1 border-t pt-3">
            <div>Conversation : <span className="mono" data-testid="studio-conv-id">{conv?.id?.slice(0, 8) || "—"}</span></div>
            <div className="flex items-center gap-1">Voix : <StatusBadge status={vstatus?.server_voice ? "configuré" : "repli navigateur"} testid="voice-backend-status" /></div>
            <div>{vstatus?.label}</div>
          </div>
        </Card>
        <Card className="flex flex-col h-[72vh] p-0 overflow-hidden">
          <div className="flex border-b">
            {[["text", "Chatbot texte", MessageSquare], ["voice", "Voix (navigateur)", Mic]].map(([v, l, I]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`studio-tab-${v}`} className={`flex-1 py-3 text-sm flex items-center justify-center gap-2 transition-colors ${tab === v ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}><I className="h-4 w-4" />{l}</button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 grid-bg" data-testid="chat-messages">
            {msgs.length === 0 && <div className="text-center text-sm text-muted-foreground mt-16">Cliquez sur « Démarrer la conversation » pour afficher l’accueil de <b>{entryAgent?.name}</b>, ou démarrez la session vocale. Le contexte est conservé entre texte et voix.</div>}
            {msgs.map((m, i) => <Bubble key={m.id || m._k || i} m={m} active={sel?.id === m.id} onSelect={setSel} onEval={setEvalMsg} />)}
            {(busy || waitingText) && <div className="text-xs text-muted-foreground" role="status" data-testid="chat-loading">{waitingText || "L’agent réfléchit…"}</div>}
            <div ref={endRef} />
          </div>
          {tab === "text" ? (
            <form onSubmit={submit} className="border-t p-3 flex gap-2">
              <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={canUse ? "Votre message…" : "Lecture seule : votre rôle ne permet pas d'utiliser le studio"} disabled={busy || !canUse || voice.state !== "idle"} />
              <Button type="submit" disabled={busy || !canUse || voice.state !== "idle"} data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
            </form>
          ) : (
            <div className="border-t p-5 flex flex-col items-center gap-3" data-testid="voice-panel">
              <div className={`orb h-20 w-20 rounded-full ${orbCls}`} style={{ transform: voice.state === "listening" ? `scale(${1 + Math.min(voice.level * 4, 0.35)})` : undefined }} data-testid="voice-orb" />
              <div className="text-sm font-semibold" data-testid="voice-state-label">{orbLabel}</div>
              <div className="flex gap-2">
                {voice.state === "idle" ? <Button onClick={voice.start} disabled={!canUse || busy || !entry} data-testid="voice-start-btn"><Mic className="h-4 w-4 mr-1" />Démarrer la session vocale</Button> : <>
                  {voice.state === "speaking" && <Button variant="outline" onClick={voice.interrupt} data-testid="voice-interrupt-btn">Interrompre</Button>}
                  <Button variant="destructive" onClick={voice.stop} data-testid="voice-stop-btn"><PhoneOff className="h-4 w-4 mr-1" />Terminer la session</Button></>}
              </div>
              <p className="text-[11px] text-muted-foreground text-center max-w-md flex gap-1"><Info className="h-3 w-3 shrink-0 mt-0.5" />Test vocal dans le navigateur (micro + haut-parleurs) — ce n'est pas un appel téléphonique réel. Parlez pendant la réponse audio pour l'interrompre. {!vstatus?.server_voice && "Mode repli : Web Speech API du navigateur (STT/TTS serveur non configurés)."}</p>
            </div>
          )}
        </Card>
        <Card className="h-[72vh] overflow-y-auto">
          <h3 className="font-semibold mb-4">Trace de la réponse</h3>
          <MessageTrace m={sel} />
        </Card>
      </div>
      {evalMsg && <EvalDialog message={evalMsg} onClose={() => setEvalMsg(null)} />}
    </div>
  );
}
